import "./assets/theme.css";
import { CssBaseline, IconButton } from "@mui/material";
import Typography from "./components/base/typography";
import { Box } from "@mui/system";
import React, { useContext, useEffect, useState } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, useNavigate } from "react-router-dom";
import reportWebVitals from "./reportWebVitals";
import RouterOutlet from "./router-outlet";
import { FullScreen, useFullScreenHandle } from "react-full-screen";
import SimpleBar from "simplebar-react";
import "simplebar-react/dist/simplebar.min.css";
import "./assets/styles/index.scss";
import UserContext, { UserProvider } from "./services/user/user";
import { FullscreenExit } from "@mui/icons-material";
import { APIProvider } from "./services/api/api";
import { IntlProvider } from "react-intl";

import {
  EcAuthProvider,
  EcAuthContext,
  EcUserDataContext,
  EcLayout,
  useSessionExpiryTimer,
  ECAxiosAPI,
  EcHeader
} from "@eclear-ag/ec-common-ui";
import "@eclear-ag/ec-common-ui/dist/index.css";

import { useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  AvatarContext,
  AvatarProvider,
  AvatarNoti
} from "@eclear-ag/ec-common-ui";
import { OverallKPIProvider } from "./services/clear-customs/overall";
import { Toast } from "./components/toast";
import SpotSidebar from "./components/spot-sidebar";
import { MAWBKPIProvider } from "./services/clear-customs/mawb";
import { ErrorBoundary } from "./ErrorBoundary";
import TraceProvider from "./tracing";
import packageJSON from "../package.json";
import enMessages from "./translations/en.json";
import deMessages from "./translations/de.json";
import esMessages from "./translations/es.json";
import frMessages from "./translations/fr.json";
// colors-v2.css (from ec-common-ui) is the single source of truth for all
// Zeplin design tokens. Imported AFTER the legacy dist/index.css bundle, which
// redefines some of the same token names (e.g. --input-*) under the same theme
// selectors — loading v2 last lets it win the cascade.
import "@eclear-ag/ec-common-ui/colors-v2.css";

// Reskin form-field styles (.onbarding-input border/hover/focus/error states).
// Imported after colors-v2.css so the design tokens it references are defined.
import "./assets/styles/onboarding.scss";

const messages = {
  en: enMessages,
  de: deMessages,
  es: esMessages,
  fr: frMessages
};

console.log("App Started! v:1.0.0");

function IndexBody() {
  const { userData } = useContext(EcUserDataContext);
  const { lang, hasConnectors } = useContext(UserContext);

  const screen1 = useFullScreenHandle();

  const navigate = useNavigate();
  const navigationProp = {
    navigate,
    routes: {
      "403role": "/spot/403-role",
      "403sub": "/spot/403-subscription",
      403: "/spot/403-role"
    }
  };

  useEffect(() => {
    console.log("SPOT v", packageJSON.version);

    if (localStorage.getItem("isDarkMode") === null) {
      if (
        window.matchMedia &&
        window.matchMedia("(prefers-color-scheme: dark)").matches
      ) {
        localStorage.setItem("isDarkMode", true);
        document.body.setAttribute("data-theme", "dark");
      } else {
        localStorage.setItem("isDarkMode", false);
        document.body.setAttribute("data-theme", "light");
      }
    } else {
      document.body.setAttribute(
        "data-theme",
        JSON.parse(localStorage.getItem("isDarkMode")) ? "dark" : "light"
      );
    }
  }, []);

  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <IntlProvider
      locale={lang.toLowerCase()}
      messages={messages[lang.toLowerCase()] || messages.en}
    >
      <EcLayout
        header={
          <EcHeader
            handleFullScreen={screen1.enter}
            hasConnectors={hasConnectors}
            EcAuthContext={EcAuthContext}
            EcAvatarContext={AvatarContext}
            useSessionExpiryTimer={useSessionExpiryTimer}
            ECAxiosAPI={ECAxiosAPI}
          />
        }
        main={
          <div>
            <FullScreen handle={screen1}>
              <RouterOutlet />
              {userData?.userInfo?.role?.includes("USER") &&
                !isMobileScreen && <AvatarNoti />}
              <Box sx={{ textAlign: "right", mt: 3 }}>
                {screen1.active ? (
                  <IconButton
                    aria-label="exit fullscreen"
                    onClick={screen1.exit}
                  >
                    <FullscreenExit />
                  </IconButton>
                ) : (
                  ""
                )}
              </Box>
            </FullScreen>
          </div>
        }
        sidebar={<SpotSidebar navigation={navigationProp} />}
      />
    </IntlProvider>
  );
}

const root = ReactDOM.createRoot(document.getElementById("root"));

root.render(
  <TraceProvider>
    <ErrorBoundary>
      <BrowserRouter>
        <APIProvider>
          <EcAuthProvider initialLoading={true}>
            <UserProvider>
              <Toast>
                <OverallKPIProvider>
                  <MAWBKPIProvider>
                    <AvatarProvider EcAuthContext={EcAuthContext}>
                      <CssBaseline />
                      <Typography component="div">
                        <SimpleBar style={{ height: "100vh" }}>
                          <IndexBody />
                        </SimpleBar>
                      </Typography>
                    </AvatarProvider>
                  </MAWBKPIProvider>
                </OverallKPIProvider>
              </Toast>
            </UserProvider>
          </EcAuthProvider>
        </APIProvider>
      </BrowserRouter>
    </ErrorBoundary>
  </TraceProvider>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();

// for hotreload the app after making changes
if (import.meta.hot) {
  import.meta.hot.accept();
}
